/**
 * Sanitization Utilities
 * XSS prevention and input sanitization for security
 */

// HTML entities to escape for XSS prevention
const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
}

/**
 * Escape HTML special characters to prevent XSS attacks
 * @param str String to escape
 * @returns Escaped string safe for HTML insertion
 */
export function escapeHtml(str: string): string {
  return str.replace(/[&<>"'`=/]/g, (char) => HTML_ESCAPE_MAP[char] ?? char)
}

/**
 * Sanitize user input - trim and escape HTML
 * @param input User input string
 * @returns Sanitized string
 */
export function sanitizeInput(input: string): string {
  return escapeHtml(input.trim())
}

/**
 * Sanitize text for display - preserves newlines but escapes HTML
 * @param text Text to sanitize
 * @returns Sanitized text with preserved line breaks
 */
export function sanitizeText(text: string): string {
  return escapeHtml(text.trim())
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('<br>')
}

/**
 * Validate and sanitize email address
 * @param email Email to validate
 * @returns Sanitized email or null if invalid
 */
export function sanitizeEmail(email: string): string | null {
  const trimmed = email.trim().toLowerCase()
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!emailRegex.test(trimmed)) {
    return null
  }

  return trimmed
}

/**
 * Validate and sanitize URL
 * @param url URL to validate
 * @returns Sanitized URL or null if invalid
 */
export function sanitizeUrl(url: string): string | null {
  const trimmed = url.trim()

  try {
    const parsed = new URL(trimmed)

    // Only allow http and https protocols
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null
    }

    return parsed.href
  } catch {
    return null
  }
}

/**
 * Sanitize filename - remove dangerous characters
 * @param filename Filename to sanitize
 * @returns Safe filename
 */
export function sanitizeFilename(filename: string): string {
  return filename
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .slice(0, 255) // Max filename length
}

/**
 * Validate image file
 * @param file File to validate
 * @param maxSizeMB Maximum file size in MB
 * @returns Validation result
 */
export function validateImageFile(
  file: File,
  maxSizeMB = 10
): { valid: boolean; error?: string } {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  const maxSize = maxSizeMB * 1024 * 1024

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.',
    }
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size is ${maxSizeMB}MB.`,
    }
  }

  return { valid: true }
}

/**
 * Strip HTML tags from a string
 * @param html String containing HTML
 * @returns Plain text without HTML tags
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent ?? ''
}

/**
 * Truncate string to a maximum length with ellipsis
 * @param str String to truncate
 * @param maxLength Maximum length
 * @returns Truncated string
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) {
    return str
  }
  return str.slice(0, maxLength - 3) + '...'
}

/**
 * Normalize whitespace in a string
 * @param str String to normalize
 * @returns String with normalized whitespace
 */
export function normalizeWhitespace(str: string): string {
  return str.replace(/\s+/g, ' ').trim()
}

/**
 * Check if string contains potentially dangerous content
 * @param str String to check
 * @returns True if potentially dangerous
 */
export function hasDangerousContent(str: string): boolean {
  const dangerousPatterns = [
    /<script\b/i,
    /javascript:/i,
    /on\w+=/i, // Event handlers like onclick=
    /data:/i,
    /<iframe\b/i,
    /<object\b/i,
    /<embed\b/i,
    /<form\b/i,
  ]

  return dangerousPatterns.some((pattern) => pattern.test(str))
}
